import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { PageLoading } from '@client/components/_ui/feedback/PageLoading';
import { AuthLoadingScreen } from '@client/components/auth/AuthLoadingScreen';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAuthStore } from '@client/stores/AuthStore';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { AlertCircle, ArrowRightLeft, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    requestedOrgId: z.coerce.bigint().optional(),
    actualOrgId: z.coerce.bigint().optional(),
    redirectTo: z.string().optional(),
});

export const Route = createFileRoute('/(authentication)/org-mismatch')({
    validateSearch: zodValidator(searchSchema),
    component: OrgMismatch,
});

function OrgMismatch() {
    const { requestedOrgId, actualOrgId, redirectTo } = Route.useSearch();
    const navigate = useNavigate();
    const authStore = useAuthStore();
    const user = useUser();
    const [isChangingOrg, setIsChangingOrg] = useState(false);

    // Find org names from user's available orgs
    const actualOrg = user.userOrgs?.find((org) => org.id === actualOrgId);

    // Determine org names for display
    const actualOrgName =
        actualOrgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE
            ? 'System console'
            : actualOrg?.name || `Organisation ${actualOrgId}`;

    const handleContinueToCorrectOrg = async () => {
        if (!actualOrgId) return;

        setIsChangingOrg(true);
        try {
            // Navigate to change-org route which will handle the organization switch
            navigate({
                to: '/change-org/$orgId',
                params: { orgId: actualOrgId },
            });
        } catch (error) {
            console.error('Error changing organization:', error);
            setIsChangingOrg(false);
        }
    };

    const handleTryDifferentAccount = () => {
        authStore.logout('org_mismatch').then(() => {
            navigate({ to: '/login', search: { orgId: requestedOrgId } });
        });
    };

    useEffect(() => {
        // If user is not authenticated at all, redirect to login
        if (!user.id) {
            navigate({ to: '/login', search: { redirectTo, orgId: requestedOrgId } });
        }
    }, [user.id]);

    if (isChangingOrg) {
        return <AuthLoadingScreen title="Switching organisation" hint={`Redirecting you to ${actualOrgName}`} />;
    }

    return (
        <PageController route={Route}>
            <PageLoading>
                <div className="w-full px-4 py-12 md:py-0">
                    <div className="mx-auto w-full max-w-lg rounded-lg border border-separator bg-card p-6 text-center sm:p-10">
                        <div className="mb-6">
                            <img
                                src="/static/curium-logo.svg"
                                alt="Curium"
                                className="mx-auto h-10 w-auto dark:invert"
                            />
                        </div>

                        <div className="flex flex-col items-center space-y-6">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-warning-subtle">
                                <AlertCircle className="h-10 w-10 text-warning" />
                            </div>

                            <div className="text-center">
                                {/* curium:allow-heading — organisation mismatch auth screen, outside the app shell */}
                                <h2 className="mb-2 font-bold text-2xl text-foreground">
                                    Organisation access mismatch
                                </h2>

                                <p className="mb-4 text-base text-muted-foreground">
                                    You don't have permission to access the requested organisation.
                                </p>

                                {actualOrgId && (
                                    <p className="mb-6 text-base text-muted-foreground">
                                        You do have access to <span className="font-semibold">{actualOrgName}</span>.
                                        Would you like to continue there instead?
                                    </p>
                                )}
                            </div>

                            <div className="flex w-full flex-col space-y-3">
                                {actualOrgId && (
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleContinueToCorrectOrg}
                                        block
                                        icon={<ArrowRightLeft />}
                                    >
                                        Continue to {actualOrgName}
                                    </Button>
                                )}

                                <Button
                                    type="secondary"
                                    size="large"
                                    onClick={handleTryDifferentAccount}
                                    block
                                    icon={<LogIn />}
                                >
                                    Try different account
                                </Button>
                            </div>

                            <div className="mt-4 text-sm text-subtle-foreground">
                                <p>
                                    If you believe you should have access to the requested organisation, please contact
                                    your administrator.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </PageLoading>
        </PageController>
    );
}
